import React, { Suspense, useEffect } from "react";
import WindroosTheme from "./theme/WindroosTheme";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import CssBaseline from "@mui/material/CssBaseline";

import VariantOnePublicShell from "./components/VariantOnePublicShell";
import VariantOneLegacyPageFrame from "./components/VariantOneLegacyPageFrame";
import RouteMetadata from "./components/RouteMetadata";
import { MigrationHoldingPage } from "./components/MigrationNotice";
import { getMigrationStatus } from "./config/migrationStatus";
import WindroosSpinner from "./components/WindroosSpinner";

//material-ui
import { ThemeProvider } from "@mui/material";
import Theme from "./theme/WindroosTheme";

// Homepage-route (de "/"-ingang) wordt eager geladen voor snelle first paint.
import StoryRoute from "./pages/StoryRoute";

// Overige publieke pagina's: code-split zodat ze niet in de homepage-bundle zitten.
const NotFound = React.lazy(() => import("./pages/404"));
const Kalender = React.lazy(() => import("./pages/Calendar"));
const Contact = React.lazy(() => import("./pages/Contact"));
const InformatieDocumenten = React.lazy(() => import("./pages/InformatieDocumenten"));
const Groepen = React.lazy(() => import("./pages/Groepen"));
const Oudervereniging = React.lazy(() => import("./pages/Oudervereniging"));
const Medezeggenschap = React.lazy(() => import("./pages/Medezeggenschap"));
const Visie = React.lazy(() => import("./pages/Visie"));
const Team = React.lazy(() => import("./pages/Team"));
const Sticht = React.lazy(() => import("./pages/Sticht"));
const Aanmelden = React.lazy(() => import("./pages/Aanmelden"));
const Vacatures = React.lazy(() => import("./pages/Vacatures"));
const Nieuws = React.lazy(() => import("./pages/Nieuws"));
const OpenDag19Juni = React.lazy(() => import("./pages/OpenDag19Juni"));
const NieuwSchoolgebouw = React.lazy(() => import("./pages/NieuwSchoolgebouw"));

// Admin-pagina's: code-split — trekt o.a. Tiptap uit de publieke bundle.
const AdminDashboard = React.lazy(() => import("./pages/admin/AdminDashboard"));
const PageContentAdmin = React.lazy(() => import("./pages/admin/PageContentAdmin"));
const MediaLibrary = React.lazy(() => import("./pages/admin/MediaLibrary"));
const StoryRouteAdmin = React.lazy(() => import("./pages/admin/StoryRouteAdmin"));
const StoryRouteSectionAdmin = React.lazy(() => import("./pages/admin/StoryRouteSectionAdmin"));
const AgendaAdmin = React.lazy(() => import("./pages/admin/AgendaAdmin"));
const AdminTutorial = React.lazy(() => import("./pages/admin/AdminTutorial"));
const NewsAdmin = React.lazy(() => import("./pages/admin/NewsAdmin"));
const PagesAdmin = React.lazy(() => import("./pages/admin/PagesAdmin"));
const DocumentsAdmin = React.lazy(() => import("./pages/admin/DocumentsAdmin"));
const TeamAdmin = React.lazy(() => import("./pages/admin/TeamAdmin"));

const theme = Theme;
const maintenanceMode = process.env.REACT_APP_MAINTENANCE_MODE === "true";
const UnderConstructionShell = React.lazy(() => import("./components/UnderConstructionShell"));
const migrationStatus = getMigrationStatus();

function RouteFallback() {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "60vh",
      }}
    >
      <WindroosSpinner label={null} />
    </div>
  );
}

function VariantOneEntry() {
  return (
    <VariantOnePublicShell>
      <StoryRoute />
    </VariantOnePublicShell>
  );
}

function OpenDagEntry() {
  return (
    <VariantOnePublicShell>
      <OpenDag19Juni />
    </VariantOnePublicShell>
  );
}

function NieuwsEntry() {
  return (
    <VariantOnePublicShell>
      <Nieuws />
    </VariantOnePublicShell>
  );
}

function CanonicalStoryRedirect() {
  const { hash, search } = useLocation();
  return <Navigate to={`/${search}${hash}`} replace />;
}

function RedirectTo({ to }) {
  return <Navigate to={to} replace />;
}

function ScrollToRouteTop() {
  const { pathname, search, hash } = useLocation();

  useEffect(() => {
    if (hash) return;

    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname, search, hash]);

  return null;
}

function LegacyShell({ children }) {
  return (
    <VariantOnePublicShell>
      <VariantOneLegacyPageFrame>{children}</VariantOneLegacyPageFrame>
    </VariantOnePublicShell>
  );
}

function NotFoundEntry() {
  return (
    <VariantOnePublicShell>
      <NotFound />
    </VariantOnePublicShell>
  );
}

function App() {
  if (migrationStatus === "pre-live") {
    return (
      <div>
        <ThemeProvider theme={WindroosTheme}>
          <CssBaseline />
          <RouteMetadata />
          <ScrollToRouteTop />
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/vacatures" element={<LegacyShell><Vacatures theme={theme} /></LegacyShell>} />
              <Route path="/contact" element={<LegacyShell><Contact theme={theme} /></LegacyShell>} />
              <Route path="*" element={<MigrationHoldingPage />} />
            </Routes>
          </Suspense>
        </ThemeProvider>
      </div>
    );
  }

  if (maintenanceMode) {
    return (
      <div>
        <ThemeProvider theme={WindroosTheme}>
          <CssBaseline />
          <RouteMetadata />
          <ScrollToRouteTop />
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<UnderConstructionShell />} />
              <Route path="/vacatures" element={<UnderConstructionShell><Vacatures theme={theme} /></UnderConstructionShell>} />
              <Route path="/contact" element={<UnderConstructionShell><Contact theme={theme} /></UnderConstructionShell>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </ThemeProvider>
      </div>
    );
  }

  return (
    <div>
      <ThemeProvider theme={WindroosTheme}>
        <CssBaseline />
        <RouteMetadata />
        <ScrollToRouteTop />
        <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* Variant 1 publieke ingang + canonieke redirects */}
          <Route path="/" element={<VariantOneEntry />} />
          <Route path="/verhaalroute" element={<CanonicalStoryRedirect />} />
          <Route path="/homepage" element={<CanonicalStoryRedirect />} />

          {/* Admin routes — geen Variant 1 shell, geen legacy chrome */}
          <Route path="/admin" element={<AdminDashboard theme={theme} />} />
          <Route path="/admin/pages/:pageName" element={<PageContentAdmin theme={theme} />} />
          <Route path="/admin/media" element={<MediaLibrary theme={theme} />} />
          <Route path="/admin/story-route" element={<StoryRouteAdmin />} />
          <Route path="/admin/story-route/:sectionName" element={<StoryRouteSectionAdmin />} />
          <Route path="/admin/agenda" element={<AgendaAdmin />} />
          <Route path="/admin/documenten" element={<DocumentsAdmin />} />
          <Route path="/admin/handleiding" element={<AdminTutorial theme={theme} />} />
          <Route path="/admin/nieuws" element={<NewsAdmin theme={theme} />} />
          <Route path="/admin/pages" element={<PagesAdmin theme={theme} />} />
          <Route path="/admin/pages/documenten" element={<Navigate to="/admin/documenten" replace />} />
          <Route path="/admin/pages/team" element={<TeamAdmin />} />

          {/* Publieke detailpagina's gebruiken Variant 1 shell; inhoud wordt gefaseerd verfijnd */}
          <Route path="/ouders" element={<RedirectTo to="/#ouders" />} />
          <Route path="/oudervereniging" element={<LegacyShell><Oudervereniging theme={theme} /></LegacyShell>} />
          <Route path="/groepsouders" element={<RedirectTo to="/oudervereniging" />} />
          <Route path="/medezeggenschap" element={<LegacyShell><Medezeggenschap theme={theme} /></LegacyShell>} />
          <Route path="/visie" element={<LegacyShell><Visie theme={theme} /></LegacyShell>} />
          <Route path="/team" element={<LegacyShell><Team theme={theme} /></LegacyShell>} />
          <Route path="/nieuw-schoolgebouw" element={<LegacyShell><NieuwSchoolgebouw /></LegacyShell>} />
          <Route path="/sticht" element={<LegacyShell><Sticht theme={theme} /></LegacyShell>} />
          <Route path="/gezondeschool" element={<RedirectTo to="/visie" />} />
          <Route path="/kalender" element={<LegacyShell><Kalender theme={theme} /></LegacyShell>} />
          <Route path="/freinet" element={<RedirectTo to="/#freinet" />} />
          <Route path="/contact" element={<LegacyShell><Contact theme={theme} /></LegacyShell>} />
          <Route path="/klas" element={<RedirectTo to="/#in-de-klas" />} />
          <Route path="/onzeschool" element={<RedirectTo to="/#school" />} />
          <Route path="/groepen" element={<LegacyShell><Groepen theme={theme} /></LegacyShell>} />
          <Route path="/groepen/:bouw" element={<LegacyShell><Groepen theme={theme} /></LegacyShell>} />
          <Route path="/nieuws/open-dag-19-juni" element={<OpenDagEntry />} />
          <Route path="/nieuws" element={<NieuwsEntry />} />
          <Route path="/siteinfo" element={<NotFoundEntry />} />
          <Route path="/vacatures" element={<LegacyShell><Vacatures theme={theme} /></LegacyShell>} />
          <Route path="/aanmelden" element={<LegacyShell><Aanmelden theme={theme} /></LegacyShell>} />
          <Route path="/inhetnieuws" element={<RedirectTo to="/nieuws" />} />
          <Route path="/documenten" element={<LegacyShell><InformatieDocumenten theme={theme} /></LegacyShell>} />
          <Route path="/addcalendar" element={<NotFoundEntry />} />
          <Route path="/development" element={<NotFoundEntry />} />

          {/* Fallback */}
          <Route path="*" element={<NotFoundEntry />} />
        </Routes>
        </Suspense>
      </ThemeProvider>
    </div>
  );
}

export default App;
